import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import AdminCreditsService from '../../../services/adminCreditsService';
import { AlertCircle, Plus, Minus, Wallet } from 'lucide-react';

const CreditsAdjustmentModal = ({ employer, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [adjustmentType, setAdjustmentType] = useState('add'); // 'add' or 'deduct'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for this adjustment');
      return;
    }

    setLoading(true);

    try {
      const adjustmentAmount = adjustmentType === 'add' 
        ? parseFloat(amount) 
        : -parseFloat(amount);

      const result = await AdminCreditsService.adjustUserCredits(
        employer.id,
        adjustmentAmount,
        reason
      );

      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Failed to adjust credits');
      }
    } catch (err) {
      setError('An error occurred while adjusting credits');
      console.error('Credit adjustment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentCredits = employer.ai_credits_remaining || 0;
  const adjustmentAmount = amount ? parseFloat(amount) : 0;
  const newCredits = adjustmentType === 'add' 
    ? currentCredits + adjustmentAmount 
    : currentCredits - adjustmentAmount;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Adjust AI Credits"
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Employer Info */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Employer:</span>
            <span className="text-sm text-gray-900 font-semibold">
              {employer.full_name || employer.email}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Current Credits:</span>
            <div className="flex items-center gap-1">
              <Wallet size={16} className="text-green-500" />
              <span className="text-sm font-bold text-gray-900">{currentCredits}</span>
            </div>
          </div>
        </div>

        {/* Adjustment Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adjustment Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAdjustmentType('add')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                adjustmentType === 'add'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <Plus size={18} />
              Add Credits
            </button>
            <button
              type="button"
              onClick={() => setAdjustmentType('deduct')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                adjustmentType === 'deduct'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              <Minus size={18} />
              Deduct Credits
            </button>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Adjustment
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Promotional credit, Compensation for technical issue, etc."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Preview */}
        {amount && (
          <div className={`p-4 rounded-lg border-2 ${
            adjustmentType === 'add' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">New Balance:</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 line-through">{currentCredits}</span>
                <span className="text-lg font-bold text-gray-900">{newCredits}</span>
              </div>
            </div>
            {newCredits < 0 && (
              <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                <AlertCircle size={16} />
                <span>Warning: This will result in a negative balance</span>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || !amount || !reason.trim()}
            className="flex-1"
          >
            {loading ? 'Processing...' : `${adjustmentType === 'add' ? 'Add' : 'Deduct'} Credits`}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreditsAdjustmentModal;
